import { useOne, useTranslate } from '@refinedev/core';
import {
  Avatar,
  Button,
  Descriptions,
  Flex,
  Skeleton,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ActivityDetailOut, ActivityOut } from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { ExecutionSourceTag } from '@/components/system/tags';
import {
  activityFieldLabel,
  actorInitials,
  activityVerbLabel,
  activityView,
  formatActivityValue,
  isEmptyValue as isEmpty,
  isLongText,
  wordDiff,
  type FieldChange,
} from './publicationActivity';

const { Text } = Typography;

const T = 'user_catalog.publications.activity';

const MOD_STATUS_COLOR: Record<string, string> = {
  enabled: 'success',
  test: 'warning',
  disabled: 'error',
};

const isModStatus = (path: string, value: unknown) =>
  path.split('.').pop() === 'mod_status' && typeof value === 'string';

/** A changed value as a chip tinted for its side of the diff. */
const ChangeValue = ({
  path,
  value,
  side,
}: {
  path: string;
  value: unknown;
  side: 'before' | 'after';
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  if (isModStatus(path, value)) {
    const status = value as string;
    return (
      <Tag color={MOD_STATUS_COLOR[status] ?? 'default'} style={{ margin: 0 }}>
        {t(`base.mod_status.${status}`, status)}
      </Tag>
    );
  }
  if (isEmpty(value)) {
    return (
      <Text type="secondary" italic>
        {t(`${T}.empty_value`)}
      </Text>
    );
  }
  const before = side === 'before';
  return (
    <Text
      style={{
        padding: `${token.paddingXXS / 2}px ${token.paddingXXS + 2}px`,
        borderRadius: token.borderRadiusSM,
        background: before ? token.colorBgLayout : token.colorSuccessBg,
        color: before ? token.red8 : token.colorSuccessText,
      }}
    >
      {formatActivityValue(t, value)}
    </Text>
  );
};

/**
 * The inline word diff a long text change shows instead of two cells: added
 * words highlighted, removed ones struck through in red.
 */
const WordDiff = ({ change }: { change: FieldChange }) => {
  const { token } = theme.useToken();
  const parts = wordDiff(String(change.old ?? ''), String(change.new ?? ''));
  return (
    <Text>
      {parts.map((part, i) => {
        if (part.kind === 'same') return part.text;
        if (part.kind === 'removed') {
          return (
            <Text key={i} delete style={{ color: token.red8 }}>
              {part.text}
            </Text>
          );
        }
        return (
          <Text
            key={i}
            style={{
              background: token.colorSuccessBgHover,
              borderRadius: token.borderRadiusXS,
            }}
          >
            {part.text}
          </Text>
        );
      })}
    </Text>
  );
};

const ChangesTable = ({ changes }: { changes: FieldChange[] }) => {
  const t = useTranslate();
  // A long text spans both value columns with its word diff.
  const span = (change: FieldChange, side: 'before' | 'after') => ({
    colSpan: isLongText(change) ? (side === 'before' ? 2 : 0) : 1,
  });
  return (
    <Table<FieldChange>
      dataSource={changes}
      rowKey="path"
      size="small"
      bordered
      pagination={false}
      columns={[
        {
          key: 'field',
          title: t(`${T}.columns.field`),
          width: 200,
          render: (_, change) => (
            <Flex vertical>
              <Text>{activityFieldLabel(t, change.path)}</Text>
              <Text type="secondary" code style={{ margin: 0 }}>
                {change.path}
              </Text>
            </Flex>
          ),
        },
        {
          key: 'before',
          title: t(`${T}.columns.before`),
          onCell: (change) => span(change, 'before'),
          render: (_, change) =>
            isLongText(change) ? (
              <WordDiff change={change} />
            ) : (
              <ChangeValue
                path={change.path}
                value={change.old}
                side="before"
              />
            ),
        },
        {
          key: 'after',
          title: t(`${T}.columns.after`),
          onCell: (change) => span(change, 'after'),
          render: (_, change) => (
            <ChangeValue path={change.path} value={change.new} side="after" />
          ),
        },
      ]}
    />
  );
};

const Actor = ({
  activity,
  detail,
}: {
  activity: ActivityOut;
  detail?: ActivityDetailOut;
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  if (activity.actor_email) {
    return (
      <Flex gap={token.marginXS} align="center">
        <Avatar size="small" style={{ fontSize: token.fontSizeSM }}>
          {actorInitials(activity.actor_email)}
        </Avatar>
        <Text>{activity.actor_email}</Text>
      </Flex>
    );
  }
  return (
    <Flex gap={token.marginXS} align="center">
      {detail?.source && <ExecutionSourceTag source={detail.source} />}
      <Text type="secondary">
        {activity.actor_id ?? t('system.activities.empty_actor')}
      </Text>
    </Flex>
  );
};

interface PublicationActivityRowProps {
  activity: ActivityOut;
  expanded: boolean;
  onToggle: () => void;
}

export const PublicationActivityRow = ({
  activity,
  expanded,
  onToggle,
}: PublicationActivityRowProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const f = (key: string) => t(`system.activities.fields.${key}`);

  // The list projection leaves `data` out; the detail loads on first expand
  // and stays cached for later ones.
  const { result: detail, query } = useOne<ActivityDetailOut>({
    resource: 'system.activities',
    id: activity.id,
    queryOptions: { enabled: expanded },
  });
  const view = activityView(activity.verb, detail?.data);

  const detailItems = [
    ...view.details.map(([key, value]) => ({
      key,
      label: t(`${T}.details.${key}`, key),
      children:
        typeof value === 'string'
          ? t(
              `${T}.detail_values.${key}.${value}`,
              formatActivityValue(t, value)
            )
          : formatActivityValue(t, value),
    })),
    ...(detail?.ip_address
      ? [
          {
            key: 'ip_address',
            label: f('ip_address'),
            children: <Text code>{detail.ip_address}</Text>,
          },
        ]
      : []),
  ];

  return (
    <div
      style={{
        padding: `${token.paddingSM}px ${token.padding}px`,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <Flex
        gap={token.margin}
        align="flex-start"
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        <Text style={{ width: 72, flexShrink: 0 }}>
          {dayjs(activity.created_at).format('HH:mm:ss')}
        </Text>

        <Flex
          gap={token.marginXS}
          align="center"
          wrap
          style={{ flex: 1, minWidth: 0 }}
        >
          <Text strong>{activityVerbLabel(t, activity.verb)}</Text>
          {activity.verb && (
            <Text code style={{ fontSize: token.fontSizeSM }}>
              {activity.verb}
            </Text>
          )}
          {view.changes.length > 0 && (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t(`${T}.fields_count`, { count: view.changes.length })}
            </Text>
          )}
        </Flex>

        <Flex gap={token.marginXS} align="center" style={{ flexShrink: 0 }}>
          <Actor activity={activity} detail={detail} />
          {/* No handler of its own: its click bubbles to the row's toggle. It
              stays for keyboard users and the expanded state. */}
          <Button
            type="text"
            size="small"
            aria-expanded={expanded}
            aria-label={t(expanded ? `${T}.collapse` : `${T}.expand`)}
            icon={expanded ? <UpOutlined /> : <DownOutlined />}
          />
        </Flex>
      </Flex>

      {expanded && (
        <Flex
          vertical
          gap={token.margin}
          style={{ marginTop: token.marginSM, marginLeft: 72 + token.margin }}
        >
          {query.isLoading && <Skeleton active paragraph={{ rows: 2 }} />}
          {query.isError && <Text type="danger">{t(`${T}.load_error`)}</Text>}
          {view.changes.length > 0 && <ChangesTable changes={view.changes} />}
          {detailItems.length > 0 && (
            <Descriptions
              size="small"
              layout="vertical"
              colon={false}
              column={{ xs: 1, sm: 2, md: 4 }}
              items={detailItems}
            />
          )}
          <Text
            style={{
              paddingTop: token.paddingSM,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              fontSize: token.fontSizeSM,
            }}
          >
            <Text type="secondary" style={{ fontSize: 'inherit' }}>
              {t(`${T}.event_id`)}
            </Text>{' '}
            <ShowLink resource="system.activities" id={activity.id} />
          </Text>
        </Flex>
      )}
    </div>
  );
};
